import { Level } from '@susu/custom-made/utils/constants';
import type { Option, OptionValue } from '@susu/custom-made/utils/interfaces';
import { changeSection } from '@susu/custom-made/utils/navigation';
import type {
  MissingMonogramValuesPerConfig,
  MissingOptionsPerConfiguration,
  MissingOptionValuesPerConfiguration,
} from '@susu/custom-made/utils/preloadConfiguration/preloadConfiguration';
import {
  getFirstSectionWithMissingValues,
  getOptionAndMatchingOptionGroupByOptionId,
  getSelectedIdsWithMissingValues,
} from '@susu/custom-made/utils/preloadConfiguration/preloadConfiguration';
import type { ConfigurationTypes } from '@susu/custom-made/utils/productDefinition';
import { isAdditonalProduct, isObjectEmpty } from '@susu/custom-made/utils/utils';
import { useCallback } from 'react';
import useNavigationContext from '../providers/hooks/useNavigationContext';
import useMissingSelectionValuesContext from '../providers/hooks/useMissingSelectionValuesContext';
import useUserContext from '../providers/hooks/useUserContext';
import useOptionContext from '../providers/hooks/useOptionContext';

export const useMissingValues = () => {
  const { setSection, setLevel } = useNavigationContext();
  const {
    missingOptionValuesPerConfig,
    missingMonogramValuesPerConfig,
    missingOptionsPerConfig,
    setShowMissingOptionsPanel,
    setMonogramsIdsWithMissingSelection,
    setMissingOptionValuesPerConfig,
    setMissingOptionsPerConfig,
    setMissingMonogramValuesPerConfig,
    setIsSummaryValidationTriggered,
  } = useMissingSelectionValuesContext();
  const { product } = useUserContext();
  const { isAdditionalProductAdded, setSelectedOption, setSelectedOptionGroup } = useOptionContext();

  const isOptionWithoutSelection = useCallback(
    (option: Option, configurationType?: ConfigurationTypes) => {
      if (!configurationType) {
        return false;
      }

      const isOptionMissing = missingOptionsPerConfig?.[configurationType]?.[option.id];
      return !!isOptionMissing;
    },
    [missingOptionsPerConfig],
  );

  const closeMissingOptionValuesPanel = useCallback(() => {
    setShowMissingOptionsPanel(false);
  }, [setShowMissingOptionsPanel]);

  const openMissingOptionValuesPanel = useCallback(() => {
    setShowMissingOptionsPanel(true);
  }, [setShowMissingOptionsPanel]);

  const redirectToFirstRelevantSection = (
    missingOptionSelection:
      | MissingOptionValuesPerConfiguration
      | MissingMonogramValuesPerConfig
      | MissingOptionsPerConfiguration,
  ) => {
    const productMatchingSection = getFirstSectionWithMissingValues(missingOptionSelection);
    changeSection(productMatchingSection, Level.Group, setSection, setLevel);
  };

  const validateConfigurationOnSummary = (
    missingOptionSelection: MissingOptionsPerConfiguration,
    missingMonogramSelection: MissingMonogramValuesPerConfig,
  ) => {
    let isConfigurationValid = true;
    if (!isObjectEmpty(missingOptionSelection)) {
      setMissingOptionsPerConfig(missingOptionSelection);
    }

    if (!isObjectEmpty(missingMonogramSelection)) {
      setMissingMonogramValuesPerConfig(missingMonogramSelection);
      setMonogramPositionIdsWithMissingValues(missingMonogramSelection);
    }

    const missingSelection = !isObjectEmpty(missingOptionSelection) ? missingOptionSelection : missingMonogramSelection;

    if (!isObjectEmpty(missingSelection)) {
      isConfigurationValid = false;

      triggerSummaryValidationFlow();
      redirectToFirstRelevantSection(missingSelection);
      openMissingOptionValuesPanel();
    }

    return isConfigurationValid;
  };

  const triggerSummaryValidationFlow = () => {
    setIsSummaryValidationTriggered(true);
  };

  const missingValueClickHandler = (e: React.BaseSyntheticEvent, options: Option[]) => {
    const { optionId, positionId, associatedSection } = e.currentTarget.dataset;

    if (optionId) {
      const matchingOptionAndGroup = getOptionAndMatchingOptionGroupByOptionId(+optionId, options);

      if (matchingOptionAndGroup?.option) {
        setSelectedOption(matchingOptionAndGroup?.option);
        if (matchingOptionAndGroup.optionGroup) {
          setSelectedOptionGroup(matchingOptionAndGroup.optionGroup);
        }

        changeSection(associatedSection, Level.Subgroup, setSection, setLevel);
      }
    }

    if (positionId) {
      changeSection(associatedSection, Level.Monogram, setSection, setLevel);
    }

    closeMissingOptionValuesPanel();
  };

  const areOptionMissingValuesEmpty = useCallback(
    (missingValues?: MissingOptionValuesPerConfiguration) => {
      const missingValuesToCheck = missingValues ?? missingOptionValuesPerConfig;

      if (!missingValuesToCheck || isObjectEmpty(missingValuesToCheck)) {
        return true;
      }

      return Object.entries(missingValuesToCheck).every(([configurationTypeId, missingOptionValue]) => {
        if (
          isAdditonalProduct(product!, configurationTypeId as ConfigurationTypes) &&
          !isAdditionalProductAdded[configurationTypeId]
        ) {
          return true;
        }
        return isObjectEmpty(missingOptionValue);
      });
    },
    [missingOptionValuesPerConfig, product, isAdditionalProductAdded],
  );

  const areMonogramMissingValuesEmpty = useCallback(
    (missingValues?: MissingMonogramValuesPerConfig) => {
      const missingValuesToCheck = missingValues ?? missingMonogramValuesPerConfig;

      if (!missingValuesToCheck || isObjectEmpty(missingValuesToCheck)) {
        return true;
      }

      return Object.values(missingValuesToCheck).every(missingMonogramValue => isObjectEmpty(missingMonogramValue));
    },
    [missingMonogramValuesPerConfig],
  );

  const areMissingOptionsEmpty = useCallback(
    (missingOptions?: MissingOptionsPerConfiguration) => {
      const missingValuesToCheck = missingOptions ?? missingOptionsPerConfig;

      if (!missingValuesToCheck || isObjectEmpty(missingValuesToCheck)) {
        return true;
      }

      return Object.entries(missingValuesToCheck).every(([configurationTypeId, missingOption]) => {
        if (
          isAdditonalProduct(product!, configurationTypeId as ConfigurationTypes) &&
          !isAdditionalProductAdded[configurationTypeId]
        ) {
          return true;
        }
        return isObjectEmpty(missingOption);
      });
    },
    [missingOptionsPerConfig, product, isAdditionalProductAdded],
  );

  const areAllConfigurationMissingValuesEmpty = useCallback(() => {
    return areOptionMissingValuesEmpty() && areMissingOptionsEmpty() && areMonogramMissingValuesEmpty();
  }, [areOptionMissingValuesEmpty, areMissingOptionsEmpty, areMonogramMissingValuesEmpty]);

  const removeOptionFromMissingValues = useCallback(
    (configurationTypeId: string, optionId: number) => {
      const isOptionStillMissing =
        missingOptionsPerConfig &&
        missingOptionsPerConfig[+configurationTypeId] &&
        missingOptionsPerConfig[+configurationTypeId][optionId];

      if (!isOptionStillMissing) {
        return;
      }

      const tmpMissingOptionsPerConfig = structuredClone(missingOptionsPerConfig);

      delete tmpMissingOptionsPerConfig[+configurationTypeId][optionId];

      if (isObjectEmpty(tmpMissingOptionsPerConfig[+configurationTypeId])) {
        delete tmpMissingOptionsPerConfig[+configurationTypeId];
      }

      setMissingOptionsPerConfig(tmpMissingOptionsPerConfig);
    },
    [missingOptionsPerConfig, setMissingOptionsPerConfig],
  );

  const removeConfigurationOptionValuesFromMissingValues = (configurationTypeId: string) => {
    const isOptionValuesStillMissing =
      missingOptionValuesPerConfig && missingOptionValuesPerConfig[+configurationTypeId];

    if (isOptionValuesStillMissing) {
      const tmpMissingOptionValuesPerConfig = structuredClone(missingOptionValuesPerConfig);

      delete tmpMissingOptionValuesPerConfig[+configurationTypeId];

      setMissingOptionValuesPerConfig(tmpMissingOptionValuesPerConfig);
    }
  };

  const removeSelectedOptionValueFromMissingValues = useCallback(
    (configurationTypeId: string, selectedOptionValue: OptionValue) => {
      removeOptionFromMissingValues(configurationTypeId, selectedOptionValue.optionId);

      const isOptionValueStillMissing =
        missingOptionValuesPerConfig &&
        missingOptionValuesPerConfig[+configurationTypeId] &&
        missingOptionValuesPerConfig[+configurationTypeId][selectedOptionValue.optionId];

      if (!isOptionValueStillMissing) {
        return;
      }

      const tmpMissingOptionValuesPerConfig = structuredClone(missingOptionValuesPerConfig);

      delete tmpMissingOptionValuesPerConfig[+configurationTypeId][selectedOptionValue.optionId];

      if (isObjectEmpty(tmpMissingOptionValuesPerConfig[+configurationTypeId])) {
        delete tmpMissingOptionValuesPerConfig[+configurationTypeId];
      }

      setMissingOptionValuesPerConfig(tmpMissingOptionValuesPerConfig);
    },
    [missingOptionValuesPerConfig, removeOptionFromMissingValues, setMissingOptionValuesPerConfig],
  );

  const removeSelectedMonogramFromMissingValues = (configurationTypeId: string, monogramPositionId: number) => {
    const isMonogramStillMissing =
      missingMonogramValuesPerConfig &&
      missingMonogramValuesPerConfig[+configurationTypeId] &&
      missingMonogramValuesPerConfig[+configurationTypeId][monogramPositionId];

    if (!isMonogramStillMissing) {
      return;
    }

    const tmpMissingMonogramValuesPerConfig = structuredClone(missingMonogramValuesPerConfig);

    delete tmpMissingMonogramValuesPerConfig[+configurationTypeId][monogramPositionId];

    if (isObjectEmpty(tmpMissingMonogramValuesPerConfig[+configurationTypeId])) {
      delete tmpMissingMonogramValuesPerConfig[+configurationTypeId];
    }

    setMissingMonogramValuesPerConfig(tmpMissingMonogramValuesPerConfig);
    setMonogramPositionIdsWithMissingValues(tmpMissingMonogramValuesPerConfig);
  };

  const setMonogramPositionIdsWithMissingValues = (missingValues: MissingMonogramValuesPerConfig) => {
    const allMissingPositionIds = getSelectedIdsWithMissingValues(missingValues);

    setMonogramsIdsWithMissingSelection(allMissingPositionIds);
  };

  return {
    closeMissingOptionValuesPanel,
    openMissingOptionValuesPanel,
    redirectToFirstRelevantSection,
    missingValueClickHandler,
    areOptionMissingValuesEmpty,
    areMonogramMissingValuesEmpty,
    areAllConfigurationMissingValuesEmpty,
    removeSelectedOptionValueFromMissingValues,
    removeConfigurationOptionValuesFromMissingValues,
    removeSelectedMonogramFromMissingValues,
    isOptionWithoutSelection,
    setMonogramPositionIdsWithMissingValues,
    setMissingOptionValuesPerConfig,
    setMissingMonogramValuesPerConfig,
    validateConfigurationOnSummary,
  };
};
